import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  SignedIn,
  SignedOut,
  SignIn,
  SignUp,
  RedirectToSignIn,
  useAuth,
  useOrganization,
  OrganizationList,
  CreateOrganization,
} from '@clerk/clerk-react';
import Dashboard from './pages/Dashboard';
import FeedPage from './pages/FeedPage';
import InstallPage from './pages/InstallPage';
import CustomizePage from './pages/CustomizePage';
import BillingPage from './pages/BillingPage';
import AdminPage from './pages/AdminPage';
import SearchEnginePage from './pages/SearchEnginePage';
import ConnectorsPage from './pages/ConnectorsPage';
import MerchandisingPage from './pages/MerchandisingPage';
import PopupsPage from './pages/PopupsPage';
import ChatPage from './pages/ChatPage';
import ChatAIPage from './pages/ChatAIPage';
import OrderMessagesPage from './pages/OrderMessagesPage';
import CustomersPage from './pages/CustomersPage';
import SupportEmailPage from './pages/SupportEmailPage';
import TeamPage from './pages/TeamPage';
import SearchesPage from './pages/SearchesPage';
import TicketPage from './pages/TicketPage';
import AnalyticsPage from './pages/AnalyticsPage';
import Layout from './components/Layout';
import LandingPage from './components/LandingPage';
import PricingPage from './pages/PricingPage';
import ReviewsPage from './pages/ReviewsPage';
import ReviewFormPage from './pages/ReviewFormPage';
import LegalPage from './pages/LegalPage';
import PrivacidadPage from './pages/PrivacidadPage';
import CookiesPage from './pages/CookiesPage';
import { api } from './lib/api';
import { Toaster } from './components/ui/sonner';
import CookieBanner from './components/CookieBanner';
import { OrgFeaturesProvider } from './hooks/useOrgFeatures';

// ── RoleGuard: redirige a /chat si el usuario es org:member ───
function RoleGuard({ children }) {
  const { membership, isLoaded } = useOrganization();
  if (!isLoaded) return null;
  // org:member solo puede acceder a /chat y /chat-ai
  if (membership && membership.role === 'org:member') {
    return <Navigate to="/chat" replace />;
  }
  return children;
}

function OrgGate({ children }) {
  const { organization, isLoaded } = useOrganization();
  const { getToken } = useAuth();

  useEffect(() => {
    // Sync user to our DB on mount
    if (getToken) {
      api.post('/api/users/sync', {}).catch(() => {});
    }
  }, [getToken]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center gap-6 p-8">
        <div className="text-center">
          <div className="text-4xl font-bold text-white mb-2">Sera</div>
          <div className="text-gray-400 text-lg">Buscador AI para tu ecommerce</div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 w-full max-w-md">
          <p className="text-gray-300 text-center mb-6">
            Crea una organización para tu tienda para comenzar
          </p>
          <div className="flex flex-col gap-3">
            <CreateOrganization
              afterCreateOrganizationUrl="/"
              appearance={{
                elements: {
                  rootBox: 'w-full',
                  card: 'bg-gray-900 border border-gray-700 shadow-none',
                  headerTitle: 'text-white',
                  headerSubtitle: 'text-gray-400',
                  formButtonPrimary: 'bg-indigo-600 hover:bg-indigo-700',
                  formFieldInput: 'bg-gray-800 border-gray-700 text-white',
                  formFieldLabel: 'text-gray-300',
                }
              }}
            />
            <OrganizationList
              hidePersonal
              afterSelectOrganizationUrl="/"
              appearance={{
                elements: {
                  rootBox: 'w-full',
                  card: 'bg-gray-900 border border-gray-700 shadow-none',
                  headerTitle: 'text-white',
                  headerSubtitle: 'text-gray-400',
                }
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return children;
}

// ── ProtectedApp: authenticated app tree; shows the marketing landing
// page instead of a hard redirect when a signed-out visitor lands on "/" ──
function ProtectedApp() {
  const location = useLocation();

  return (
    <>
      <SignedIn>
        <OrgGate>
          <OrgFeaturesProvider>
          <Layout>
            <Routes>
              <Route path="/" element={<RoleGuard><Dashboard /></RoleGuard>} />
              <Route path="/feed" element={<RoleGuard><FeedPage /></RoleGuard>} />
              <Route path="/install" element={<RoleGuard><InstallPage /></RoleGuard>} />
              <Route path="/customize" element={<RoleGuard><CustomizePage /></RoleGuard>} />
              <Route path="/billing" element={<RoleGuard><BillingPage /></RoleGuard>} />
              <Route path="/search-engine" element={<RoleGuard><SearchEnginePage /></RoleGuard>} />
              <Route path="/connectors" element={<RoleGuard><ConnectorsPage /></RoleGuard>} />
              <Route path="/merchandising" element={<RoleGuard><MerchandisingPage /></RoleGuard>} />
              <Route path="/popups" element={<RoleGuard><PopupsPage /></RoleGuard>} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat-ai" element={<RoleGuard><ChatAIPage /></RoleGuard>} />
          <Route path="/order-messages" element={<OrderMessagesPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/support-email" element={<SupportEmailPage />} />
          <Route path="/searches" element={<RoleGuard><SearchesPage /></RoleGuard>} />
          <Route path="/team" element={<RoleGuard><TeamPage /></RoleGuard>} />
          <Route path="/analytics" element={<RoleGuard><AnalyticsPage /></RoleGuard>} />
          <Route path="/reviews" element={<ReviewsPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
          </OrgFeaturesProvider>
        </OrgGate>
      </SignedIn>
      <SignedOut>
        {location.pathname === '/' ? <LandingPage /> : <RedirectToSignIn />}
      </SignedOut>
    </>
  );
}

export default function App() {
  return (
    <>
      <Routes>
        {/* Public ticket portal (no auth) */}
        <Route path="/t/:token" element={<TicketPage />} />

        {/* Public review form (no auth) */}
        <Route path="/reviews/:token" element={<ReviewFormPage />} />

        {/* Public marketing pricing page (no auth) */}
        <Route path="/precios" element={<PricingPage />} />

        {/* Public legal pages (no auth) */}
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/privacidad" element={<PrivacidadPage />} />
        <Route path="/cookies" element={<CookiesPage />} />

        {/* Auth routes */}
        <Route
          path="/sign-in/*"
          element={
            <div className="min-h-screen bg-gray-950 flex items-center justify-center">
              <SignIn
                routing="path"
                path="/sign-in"
                fallbackRedirectUrl="/"
                appearance={{
                  elements: {
                    rootBox: '',
                    card: 'bg-gray-900 border border-gray-700',
                    headerTitle: 'text-white',
                    headerSubtitle: 'text-gray-400',
                    socialButtonsBlockButton: 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700',
                    formButtonPrimary: 'bg-indigo-600 hover:bg-indigo-700',
                    formFieldInput: 'bg-gray-800 border-gray-700 text-white',
                    formFieldLabel: 'text-gray-300',
                    footer: 'hidden',
                  }
                }}
              />
            </div>
          }
        />
        <Route
          path="/sign-up/*"
          element={
            <div className="min-h-screen bg-gray-950 flex items-center justify-center">
              <SignUp
                routing="path"
                path="/sign-up"
                fallbackRedirectUrl="/"
                appearance={{
                  elements: {
                    rootBox: '',
                    card: 'bg-gray-900 border border-gray-700',
                    headerTitle: 'text-white',
                    headerSubtitle: 'text-gray-400',
                    socialButtonsBlockButton: 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700',
                    formButtonPrimary: 'bg-indigo-600 hover:bg-indigo-700',
                    formFieldInput: 'bg-gray-800 border-gray-700 text-white',
                    formFieldLabel: 'text-gray-300',
                  }
                }}
              />
            </div>
          }
        />

        {/* Protected app routes */}
        <Route path="/*" element={<ProtectedApp />} />
      </Routes>
      <Toaster position="bottom-right" richColors closeButton />
      <CookieBanner />
    </>
  );
}
